import type { ComponentPropsWithoutRef } from "react"
import React, { forwardRef, memo } from "react"
import { IconCheckOutline18 as CheckIcon } from "nucleo-ui-outline-18"

import { cn } from "@productlane/lib/src/browser"

import { Badge } from "./badge"
import {
	InternalTooltip,
	InternalTooltipContent,
	InternalTooltipTrigger,
} from "./internal/tooltip"
import { planAiCredits, planResolutions } from "./plans-data"

export { planAiCredits, planResolutions } from "./plans-data"

// Tooltip content based on Productlane pricing page
export const featureTooltips: Record<string, string> = {
	Users: "Number of team members who can access and manage the workspace",
	Viewers: "Additional team members with read-only access to view content",
	"AI Assistant":
		"An in-thread assistant that researches and drafts replies, grounded in your help center and past threads",
	GDPR: "Full GDPR compliance with data protection and privacy controls",
	"CSV Import": "Import customer data and feedback from CSV files",
	"Zendesk Import": "Migrate your existing Zendesk data to Productlane",
	"White-glove Docs Migration":
		"Assistance to migrate your docs, support and feedback",
	"Priority Support": "Faster response times and dedicated support channels",
	"Shared Slack Connect Channel":
		"A dedicated Slack Connect channel with our team for priority support",
	"Support Widget":
		"Embeddable widget for AI Chat, Roadmap, Changelog, and Support Portal",
	"Response Times": "Set and track response time goals for customer inquiries",
	"Shared Slack Inbox":
		"Centralize your customer Slack connect channels and answer within Productlane",
	"Shared Email Inbox": "Unified email inbox for team collaboration on support",
	Docs: "Create and manage help center documentation",
	Widget:
		"A seamlessly embeddable support widget that brings your product information and customer help directly to your website",
	"Productlane Agent":
		"AI-powered support agent that answers customer questions, checks bug reports against your product data, and files the Linear issue",
	"Additional resolutions":
		"Extra AI resolutions beyond your plan's included amount at $0.79 each",
	"AI credits":
		"Monthly credits for token-based AI: the AI Assistant, docs, and translation. Usage past the included amount is billed at cost plus a small margin.",
	"Email Signatures": "Custom email signatures for team members",
	Tags: "Organize and categorize conversations with custom tags",
	"Custom Data": "Add custom fields and data to customer profiles",
	"Customer Support Portal":
		"Self-service portal for customers to find their Linear requests",
	Macros: "Pre-written responses to reply faster",
	SLAs: "Set service level agreements for response and resolution times",
	"Auto-Assignments":
		"Automatically assign conversations to the right team members",
	"Custom Views": "Create custom filtered views for your support inbox",
	Tiers:
		"Synced with Linear tiers to categorize customers and filter conversations by tier",
	"Slack Notifications":
		"Get notified about new conversations and updates in Slack, with support for custom notification channels",
	"Automatic Issue Creation":
		"The Productlane Agent automatically creates and categorizes Linear issues from customer conversations",
	"Office Hours": "Set available hours for your support team",
	"Auto-Responders":
		"Automatically reply to customers based on rules and schedules",
	Reporting: "Track key support metrics and team performance",
	"Advanced Reporting": "In-depth analytics with custom reports and dashboards",
	"Live Chat": "Real-time chat support with your team directly from the widget",
	"Public Roadmap":
		"Turn your Linear into a feedback community or just share your roadmap with customers",
	Changelog:
		"Intelligent changelog generated automatically from Linear issues, projects, and GitHub commits",
	"Feedback Widget": "Collect feedback directly from your product",
	"Custom theme": "Customize the look and feel of your public pages",
	"Private Roadmap":
		"Whitelist only certain email domains for access to your portal",
	"Multiple portals":
		"Run multiple portals under the same domain — each with its own URL slug, branding, and docs/changelog content",
	"SSO (JWT)":
		"Single Sign-On integration with JWTs, so your users don't need to login",
	"Custom domain": "Use your own domain for your portal",
	"In-App Changelog Notifications":
		"Notify users about updates within your app",
	"API Access": "Full API access for custom integrations",
	"Whitelabel Widget":
		"Remove Productlane branding the widget, portal and emails",
	"Multi-Language":
		"Change the language for your public pages, widget, changelog and help center articles",
	"Private Changelog": "Internal changelog for team-only updates",
	Broadcast:
		"Send targeted email broadcasts to customers about product updates, announcements, and changelog entries",
	"AI Changelog":
		"AI-powered changelog generation from Linear issues, completed projects, and GitHub commits",
	"Self-updating Help Center":
		"Keeps your help center up to date based on historic conversations, completed Linear issues, and published changelogs",
	"AI Drafts":
		"Every new thread arrives with a reply already written in the composer, grounded in your docs, changelog and roadmap. A teammate reads it and sends it.",
	Routines:
		"A prompt that runs on every new thread as its messages arrive, so it tags, prioritizes and routes the thread for you",
	Linear: "The most advanced Linear integration in customer support",
	Zapier: "Connect with 5000+ apps via Zapier automation",
	Slack: "Integrate with Slack for notifications and workflows",
	Raycast: "Quick access to Productlane via Raycast extension",
	API: "REST API for custom integrations and automation",
	MCP: "Connect AI assistants like Claude and Cursor to your workspace through the Model Context Protocol server",
	HubSpot: "Sync conversations from Productlane to HubSpot",
	Attio: "Sync conversations from Productlane to Attio",
	"Microsoft Teams":
		"Sync customer messages from Microsoft Teams to Productlane",
}

export const FeatureWithTooltip = ({
	feature,
	children,
}: {
	feature: string
	children: React.ReactNode
}) => {
	const tooltipContent = featureTooltips[feature]

	if (!tooltipContent) {
		return <div className="flex items-center gap-1">{children}</div>
	}

	return (
		<InternalTooltip delayDuration={100}>
			<InternalTooltipTrigger asChild>
				<span className="cursor-default underline decoration-gray-300 decoration-dotted underline-offset-4 transition-colors hover:decoration-gray-500 dark:decoration-gray-600 dark:hover:decoration-gray-400">
					{children}
				</span>
			</InternalTooltipTrigger>
			<InternalTooltipContent
				side="right"
				align="center"
				className="w-full max-w-[50rem] !animate-none text-[13px] data-[state=closed]:duration-100 data-[state=delayed-open]:duration-150 data-[state=delayed-open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=delayed-open]:fade-in-0 data-[state=closed]:slide-out-to-left-1 data-[state=delayed-open]:slide-in-from-left-1"
			>
				{tooltipContent}
			</InternalTooltipContent>
		</InternalTooltip>
	)
}

// 3 = the self-serve tiers, 4 = plus Enterprise on the public pricing page.
export type PlanColumnCount = 3 | 4

const PlanColumnsContext = React.createContext<PlanColumnCount>(3)

export function PlansGrid({
	columns = 3,
	activeCol,
	className,
	children,
}: {
	columns?: PlanColumnCount
	activeCol?: string
	className?: string
	children: React.ReactNode
}) {
	return (
		<PlanColumnsContext.Provider value={columns}>
			<div
				data-cols={columns}
				data-active-col={activeCol}
				className={cn("plans-grid", className)}
			>
				{children}
			</div>
		</PlanColumnsContext.Provider>
	)
}

export const PlanRow = forwardRef<
	HTMLDivElement,
	ComponentPropsWithoutRef<"div">
>(({ className, ...props }, ref) => {
	const columns = React.useContext(PlanColumnsContext)

	return (
		<div
			ref={ref}
			className={cn(
				"group grid grid-cols-12 gap-x-4 lg:gap-x-8",
				columns === 4 ? "lg:grid-cols-12" : "lg:grid-cols-10",
				className,
			)}
			{...props}
		/>
	)
})

const Cell = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(
	({ className, ...props }, ref) => {
		return (
			<div
				ref={ref}
				className={cn(
					"border-b border-gray-150 py-3 dark:border-white/[0.03]",
					className,
				)}
				{...props}
			/>
		)
	},
)

const PlanRowHeader = forwardRef<
	HTMLDivElement,
	ComponentPropsWithoutRef<"div">
>(({ className, ...props }, ref) => {
	return (
		<div
			ref={ref}
			className={cn(
				"col-span-full border-b border-gray-150 py-2.5 text-sm font-bold text-secondary lg:col-span-4 lg:font-normal dark:border-white/[0.08]",
				className,
			)}
			{...props}
		/>
	)
})

const PlanRowEntry = forwardRef<
	HTMLDivElement,
	ComponentPropsWithoutRef<"div">
>(({ className, ...props }, ref) => {
	const columns = React.useContext(PlanColumnsContext)

	return (
		<div
			ref={ref}
			className={cn(
				"flex items-start justify-start gap-3 border-b border-gray-150 py-2.5 text-sm text-gray-500 lg:col-span-2 dark:border-white/[0.08] dark:text-gray-400",
				columns === 4 ? "col-span-3" : "col-span-4",
				className,
			)}
			{...props}
		/>
	)
})

const PlanRowSectionHeader = forwardRef<
	HTMLDivElement,
	ComponentPropsWithoutRef<"div">
>(({ className, ...props }, ref) => {
	return (
		<div
			ref={ref}
			className={cn(
				"col-span-full mt-9 py-3 pb-1.5 text-lg font-bold text-gray-800 lg:col-span-4 lg:mt-6 lg:!font-semibold dark:text-gray-200",
				className,
			)}
			{...props}
		/>
	)
})

// Attach sub-components
Object.assign(PlanRow, {
	Entry: PlanRowEntry,
	Header: PlanRowHeader,
	SectionHeader: PlanRowSectionHeader,
	Cell,
})

// Type the compound component
export const Row = PlanRow as typeof PlanRow & {
	Entry: typeof PlanRowEntry
	Header: typeof PlanRowHeader
	SectionHeader: typeof PlanRowSectionHeader
	Cell: typeof Cell
}

const Yes = <CheckIcon className="inline h-5 w-5 text-primary" />
const Soon = (
	<Badge variant="green" size="sm" className="ml-1.5">
		Soon
	</Badge>
)

export type PlanTier = "starter" | "pro" | "scale"

// false = not included, true = included, string = included with a detail label.
export type PlanFeatureValue = boolean | string

export interface PlanFeature {
	feature: string
	label: string
	soon?: boolean
	starter: PlanFeatureValue
	pro: PlanFeatureValue
	scale: PlanFeatureValue
	enterprise: PlanFeatureValue
	// Self-describing label for the upgrade card, where the feature name plus
	// the quantity would wrap onto a second line.
	benefitLabel?: Record<PlanTier, string>
}

export interface PlanFeatureSection {
	title: string
	features: PlanFeature[]
}

const all = (value: PlanFeatureValue = true) => ({
	starter: value,
	pro: value,
	scale: value,
	enterprise: value,
})
const proAndUp = { starter: false, pro: true, scale: true, enterprise: true }
const scaleAndUp = { starter: false, pro: false, scale: true, enterprise: true }
const enterpriseOnly = {
	starter: false,
	pro: false,
	scale: false,
	enterprise: true,
}

export const PLAN_FEATURE_SECTIONS: PlanFeatureSection[] = [
	{
		title: "AI",
		features: [
			{
				feature: "Productlane Agent",
				label: "Productlane Agent",
				starter: `${planResolutions.Starter.included} resolutions`,
				pro: `${planResolutions.Pro.included} resolutions`,
				scale: `${planResolutions.Scale.included} resolutions`,
				enterprise: "Custom volume",
				benefitLabel: {
					starter: `${planResolutions.Starter.included} AI resolutions`,
					pro: `${planResolutions.Pro.included} AI resolutions`,
					scale: `${planResolutions.Scale.included} AI resolutions`,
				},
			},
			{
				feature: "AI credits",
				label: "Included AI credits",
				starter: `$${planAiCredits.Starter}/mo`,
				pro: `$${planAiCredits.Pro}/mo`,
				scale: `$${planAiCredits.Scale}/mo`,
				enterprise: "Custom",
			},
			{
				feature: "Additional resolutions",
				label: "Additional resolutions",
				...all(`$${planResolutions.Starter.pricePerResolution}/resolution`),
				enterprise: "Volume pricing",
			},
			{
				feature: "Automatic Issue Creation",
				label: "Automatic Issue Creation",
				...all(),
			},
			{
				feature: "Self-updating Help Center",
				label: "Self-updating Help Center",
				...all(),
			},
			{ feature: "AI Assistant", label: "AI Assistant", ...all() },
			{ feature: "AI Drafts", label: "AI Drafts", ...all() },
			{ feature: "Routines", label: "Routines", ...all() },
			{
				feature: "AI Changelog",
				label: "Release Intelligence",
				...proAndUp,
			},
		],
	},
	{
		title: "Channels",
		features: [
			{ feature: "Shared Email Inbox", label: "Email", ...all() },
			{ feature: "Live Chat", label: "Live Chat", ...all() },
			{ feature: "Widget", label: "Widget", ...all() },
			{ feature: "Shared Slack Inbox", label: "Slack", ...proAndUp },
			{
				feature: "Microsoft Teams",
				label: "Microsoft Teams",
				...scaleAndUp,
			},
		],
	},
	{
		title: "Workflows",
		features: [
			{ feature: "Office Hours", label: "Office Hours", ...all() },
			{ feature: "Auto-Responders", label: "Auto-Responders", ...all() },
			{ feature: "Reporting", label: "Reporting", ...all() },
			{ feature: "Custom Views", label: "Custom Views", ...all() },
			{ feature: "Tiers", label: "Tiers", ...all() },
			{
				feature: "Slack Notifications",
				label: "Slack Notifications",
				...all(),
			},
			{ feature: "Email Signatures", label: "Email Signatures", ...all() },
			{ feature: "Custom Data", label: "Custom Data", ...all() },
			{ feature: "Macros", label: "Macros", ...proAndUp },
			{
				feature: "Customer Support Portal",
				label: "Customer Support Portal",
				...scaleAndUp,
			},
			{ feature: "Auto-Assignments", label: "Auto-Assignments", ...scaleAndUp },
			{ feature: "SLAs", label: "SLAs", ...scaleAndUp },
		],
	},
	{
		title: "Public pages",
		features: [
			{ feature: "Docs", label: "Help Center", ...all() },
			{ feature: "Public Roadmap", label: "Feature requests", ...all() },
			{ feature: "Changelog", label: "Intelligent Changelog", ...all() },
			{ feature: "Custom theme", label: "Custom theme", ...all() },
			{ feature: "Custom domain", label: "Custom domain", ...all() },
			{
				feature: "Whitelabel Widget",
				label: "Remove Productlane branding",
				...scaleAndUp,
			},
			{
				feature: "In-App Changelog Notifications",
				label: "In-App Changelog Notifications",
				...all(),
			},
			{ feature: "Broadcast", label: "Email Broadcasts", ...proAndUp },
			{ feature: "Broadcast", label: "Slack Broadcasts", ...proAndUp },
			{ feature: "Multi-Language", label: "Multi-Language", ...proAndUp },
			{ feature: "Private Roadmap", label: "Private portal", ...scaleAndUp },
			{ feature: "Multiple portals", label: "Multiple portals", ...scaleAndUp },
			{ feature: "SSO (JWT)", label: "SSO (JWT)", ...scaleAndUp },
		],
	},
	{
		title: "Integrations",
		features: [
			{ feature: "Linear", label: "Linear", ...all() },
			{ feature: "Zapier", label: "Zapier", ...all() },
			{ feature: "Raycast", label: "Raycast", ...all() },
			{ feature: "API", label: "API", ...all() },
			{ feature: "MCP", label: "MCP", ...all() },
			{ feature: "Attio", label: "Attio", ...scaleAndUp },
			{ feature: "HubSpot", label: "HubSpot", ...scaleAndUp },
		],
	},
	{
		title: "General",
		features: [
			{ feature: "GDPR", label: "GDPR", ...all() },
			{ feature: "CSV Import", label: "CSV Import", ...all() },
			{ feature: "Viewers", label: "Free Viewers", ...all() },
			{ feature: "Zendesk Import", label: "Zendesk Importer", ...proAndUp },
			{ feature: "Priority Support", label: "Priority Support", ...scaleAndUp },
			{
				feature: "Shared Slack Connect Channel",
				label: "Shared Slack Connect Channel",
				...scaleAndUp,
			},
			{
				feature: "White-glove Docs Migration",
				label: "White-glove Migration",
				...scaleAndUp,
			},
			{
				feature: "Custom contract",
				label: "Invoicing and custom terms",
				...enterpriseOnly,
			},
			{
				feature: "Security review",
				label: "Security review and DPA",
				...enterpriseOnly,
			},
			{
				feature: "Dedicated onboarding",
				label: "Dedicated onboarding",
				...enterpriseOnly,
			},
		],
	},
]

export interface PlanUpgradeBenefit {
	feature: string
	label: string
	detail?: string
}

// Features the target tier adds over the current one. A tier that raises a
// quantity (resolutions, credits) counts as an upgrade too.
export const planUpgradeBenefits = (
	from: PlanTier | "free",
	to: PlanTier,
): PlanUpgradeBenefit[] => {
	const benefits: PlanUpgradeBenefit[] = []
	for (const section of PLAN_FEATURE_SECTIONS) {
		for (const feature of section.features) {
			const target = feature[to]
			if (!target) continue
			const current = from === "free" ? false : feature[from]
			if (current === target) continue
			const benefitLabel = feature.benefitLabel?.[to]
			benefits.push({
				feature: feature.feature,
				label: benefitLabel ?? feature.label,
				detail: benefitLabel || typeof target !== "string" ? undefined : target,
			})
		}
	}
	return benefits
}

const FeatureCell = ({ value }: { value: PlanFeatureValue }) => (
	<Row.Entry>
		{!!value && Yes}
		{typeof value === "string" && ` ${value}`}
	</Row.Entry>
)

// An Enterprise-only row would render as an empty line without that column.
export const visiblePlanFeatures = (
	features: PlanFeature[],
	columns: PlanColumnCount,
) =>
	columns === 4
		? features
		: features.filter((f) => f.starter || f.pro || f.scale)

export const PlanData = memo(() => {
	const columns = React.useContext(PlanColumnsContext)

	return (
		<>
			{PLAN_FEATURE_SECTIONS.map((section) => (
				<React.Fragment key={section.title}>
					<Row>
						<Row.SectionHeader>{section.title}</Row.SectionHeader>
					</Row>
					<Row>
						{visiblePlanFeatures(section.features, columns).map((feature) => (
							<React.Fragment key={feature.label}>
								<Row.Header>
									<FeatureWithTooltip feature={feature.feature}>
										{feature.label}
										{feature.soon && <> {Soon}</>}
									</FeatureWithTooltip>
								</Row.Header>
								<FeatureCell value={feature.starter} />
								<FeatureCell value={feature.pro} />
								<FeatureCell value={feature.scale} />
								{columns === 4 && <FeatureCell value={feature.enterprise} />}
							</React.Fragment>
						))}
					</Row>
				</React.Fragment>
			))}
		</>
	)
})
